import React, { useState, useEffect } from 'react';

type WithCharCounterProps = {
    maxLength: number;
    placeholder?: string;
    className?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export function withCharCounter<
    T extends HTMLTextAreaElement | HTMLInputElement,
    P extends React.InputHTMLAttributes<T>,
>(WrappedComponent: React.ComponentType<P>) {
    const ComponentWithCounter = React.forwardRef<T, P & WithCharCounterProps>((props, ref) => {
        const {
            maxLength,
            placeholder,
            className,
            value: controlledValue,
            onChange,
            ...rest
        } = props;

        const [value, setValue] = useState(controlledValue ?? '');

        useEffect(() => {
            if (typeof controlledValue === 'string') {
                setValue(controlledValue);
            }
        }, [controlledValue]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setValue(e.target.value);
            onChange?.(e);
        };

        return (
            <div className='relative w-full'>
                <WrappedComponent
                    {...(rest as unknown as P)}
                    ref={ref}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    className={className}
                    value={value}
                    onChange={handleChange}
                />
                <div className='absolute bottom-[-30px] right-2 text-xs text-content-secondary'>
                    {value.toString().length}/{maxLength}
                </div>
            </div>
        );
    });

    ComponentWithCounter.displayName = `withCharCounter(${
        WrappedComponent.displayName || WrappedComponent.name || 'Component'
    })`;

    return ComponentWithCounter;
}
